'use client';

import { Link } from '@/i18n/navigation';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';

import { useLocale, useTranslations } from 'next-intl';
import {
  createLeadMagnetSchema,
  detectContactType,
  type LeadMagnetInput,
} from '@/lib/schemas/lead-magnet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { track } from '@/lib/analytics/ym';

const PDF_URL = '/lead-magnets/readiness-checklist.pdf';

function triggerDownload() {
  const a = document.createElement('a');
  a.href = PDF_URL;
  a.download = 'readiness-checklist.pdf';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="text-sm text-destructive">{message}</p>;
}

export function EmailGateForm() {
  const t = useTranslations('LeadMagnet.form');
  const locale = useLocale();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contactType, setContactType] = useState<'email' | 'telegram' | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LeadMagnetInput>({
    resolver: zodResolver(
      createLeadMagnetSchema({
        contactRequired: t('errors.contactRequired'),
        contactInvalid: t('errors.contactInvalid'),
        consent: t('errors.consent'),
      }),
    ),
    mode: 'onTouched',
    defaultValues: { contact: '' } as unknown as LeadMagnetInput,
  });

  const onSubmit = async (data: LeadMagnetInput) => {
    setSubmitting(true);
    try {
      const res = await fetch('/api/lead-magnet', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...data, locale }),
      });
      if (res.status === 429) {
        toast.warning(t('toast.rateLimited'));
        return;
      }
      if (!res.ok) {
        toast.error(t('toast.failed'));
        return;
      }
      const type = detectContactType(data.contact);
      setContactType(type);
      setSubmitted(true);
      triggerDownload();
      track.formSubmit({ form: 'lead_magnet' });
      track.leadMagnetDownload('email_gate');
    } catch {
      toast.error(t('toast.failed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="mt-6 space-y-3">
        <p className="text-sm text-muted-foreground">
          {contactType === 'email' ? t('doneEmail') : t('doneTelegram')}
        </p>
        <p className="text-sm text-muted-foreground">
          {t.rich('fallback', {
            here: (chunks) => (
              <a
                href={PDF_URL}
                download="readiness-checklist.pdf"
                className="underline underline-offset-4 hover:text-foreground"
              >
                {chunks}
              </a>
            ),
          })}
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4" noValidate>
      <div className="grid gap-2">
        <Label htmlFor="lm-contact">{t('contactLabel')}</Label>
        <Input
          id="lm-contact"
          type="text"
          autoComplete="email"
          placeholder={t('contactPlaceholder')}
          aria-invalid={!!errors.contact}
          {...register('contact')}
        />
        <FieldError message={errors.contact?.message} />
      </div>

      <div className="flex items-start gap-3">
        <input
          id="lm-consent"
          type="checkbox"
          className="mt-1 h-4 w-4 shrink-0 rounded border-input accent-foreground"
          aria-invalid={!!errors.consent}
          {...register('consent')}
        />
        <Label
          htmlFor="lm-consent"
          className="text-sm text-muted-foreground leading-snug"
        >
          <span>
            {t.rich('consent', {
              law: (chunks) => (
                <Link
                  href="/privacy"
                  className="underline underline-offset-4 hover:text-foreground"
                >
                  {chunks}
                </Link>
              ),
            })}
          </span>
        </Label>
      </div>
      <FieldError message={errors.consent?.message} />

      <button
        type="submit"
        disabled={submitting}
        className={cn(
          buttonVariants({ size: 'lg' }),
          'min-h-12 w-full text-base md:w-auto',
        )}
      >
        {submitting ? t('submitting') : t('submit')}
      </button>
    </form>
  );
}
